// SigningOverlay v1 — fresh file, fresh name, always-visible fields
import { useState, useCallback } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { supabase } from '../../../lib/supabase';

interface Props {
  clientId: string;
  clientName: string;
  clientEmail: string;
  company: string;
  templateUrl: string;
  initialImporterOfRecord?: string;
  onSigned: (data: { letterUrl: string; signedAt: string }) => void;
  onClose: () => void;
}

type Step = 'review' | 'processing' | 'done';

export default function SigningOverlay({
  clientId,
  clientName,
  clientEmail,
  company,
  templateUrl,
  initialImporterOfRecord,
  onSigned,
  onClose,
}: Props) {
  const [step, setStep] = useState<Step>('review');
  const [importerOfRecord, setImporterOfRecord] = useState(initialImporterOfRecord || company || '');
  const [authorizedSigner, setAuthorizedSigner] = useState(clientName || '');
  const [hasRead, setHasRead] = useState(false);
  const [consentEsign, setConsentEsign] = useState(false);
  const [consentIntent, setConsentIntent] = useState(false);
  const [error, setError] = useState('');
  const [signedUrl, setSignedUrl] = useState('');

  const isFormValid =
    importerOfRecord.trim().length >= 1 &&
    authorizedSigner.trim().length >= 2 &&
    hasRead &&
    consentEsign &&
    consentIntent;

  const handleSign = useCallback(async () => {
    if (!isFormValid) return;
    setStep('processing');
    setError('');

    try {
      let ipAddress = 'Unavailable';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ipAddress = ipData.ip || 'Unavailable';
      } catch {
        ipAddress = 'Unavailable';
      }

      const userAgent = navigator.userAgent;
      const signedAt = new Date().toISOString();

      let pdfDoc: PDFDocument;

      if (templateUrl) {
        const templateRes = await fetch(templateUrl);
        if (!templateRes.ok) throw new Error('Could not load the engagement letter template. Please contact support.');
        const templateBytes = await templateRes.arrayBuffer();
        pdfDoc = await PDFDocument.load(templateBytes);
      } else {
        // No template uploaded yet — create a blank PDF with notice
        pdfDoc = await PDFDocument.create();
        const blankPage = pdfDoc.addPage([612, 792]);
        const hFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        blankPage.drawText('ENGAGEMENT LETTER', { x: 50, y: 720, size: 18, font: hFont, color: rgb(0.05, 0.09, 0.13) });
        blankPage.drawText('Template pending upload by attorney. Signature recorded below.', { x: 50, y: 690, size: 11, font: hFont, color: rgb(0.4, 0.4, 0.4) });
      }

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const auditPage = pdfDoc.addPage([612, 792]);
      const pageWidth = 612;
      const margin = 50;
      let curY = 742;

      const write = (text: string, size = 10, bold = false, colorRgb = rgb(0.05, 0.05, 0.05)) => {
        auditPage.drawText(text, { x: margin, y: curY, size, font: bold ? boldFont : font, color: colorRgb });
        curY -= size + 7;
      };

      const writeLine = () => {
        auditPage.drawLine({
          start: { x: margin, y: curY },
          end: { x: pageWidth - margin, y: curY },
          thickness: 0.5,
          color: rgb(0.75, 0.75, 0.75),
        });
        curY -= 14;
      };

      auditPage.drawRectangle({ x: 0, y: 742, width: 612, height: 50, color: rgb(0.05, 0.09, 0.13) });
      auditPage.drawText('ELECTRONIC SIGNATURE AUDIT TRAIL', {
        x: margin, y: 762, size: 13, font: boldFont, color: rgb(0.992, 0.725, 0.075),
      });
      curY = 720;

      write('Compliance Notice', 11, true, rgb(0.05, 0.09, 0.13));
      write('This document was electronically signed in full compliance with:', 9);
      write('\u2022  Electronic Signatures in Global and National Commerce Act (ESIGN Act, 15 U.S.C. \u00a7 7001)', 9);
      write('\u2022  Uniform Electronic Transactions Act (UETA)', 9);
      write('\u2022  FIPS 140-3 encrypted transmission via Supabase (AES-256)', 9);
      curY -= 4;
      writeLine();

      write('SIGNATURE RECORD', 11, true, rgb(0.05, 0.09, 0.13));
      curY -= 2;
      write(`Importer of Record:        ${importerOfRecord}`, 10);
      write(`Authorized Signer:         ${authorizedSigner}`, 10);
      write(`Account / Identity:        ${clientEmail}`, 10);
      write(`Date & Time (ISO 8601):    ${signedAt}`, 10);
      write(`IP Address:                ${ipAddress}`, 10);
      write(`Authentication:            Supabase Email/Password (MFA-capable)`, 10);
      write(`User Agent:                ${userAgent.substring(0, 85)}`, 9, false, rgb(0.35, 0.35, 0.35));
      curY -= 4;
      writeLine();

      write('INTENT & CONSENT CHECKBOXES (all checked by signer)', 11, true, rgb(0.05, 0.09, 0.13));
      curY -= 2;
      write('[X] I have carefully read and understood this Engagement Letter in its entirety.', 10);
      write('[X] I consent to electronic signatures and transactions under ESIGN Act \u00a7 7001 and UETA.', 10);
      write('[X] I expressly intend this electronic signature to constitute my legally binding signature.', 10);
      curY -= 4;
      writeLine();

      write('ELECTRONIC SIGNATURE', 11, true, rgb(0.05, 0.09, 0.13));
      curY -= 4;

      write('Importer of Record:', 9, true, rgb(0.3, 0.3, 0.3));
      auditPage.drawText(importerOfRecord, {
        x: margin, y: curY, size: 14, font: boldFont, color: rgb(0.05, 0.09, 0.13),
      });
      curY -= 22;

      write('Authorized Signer on behalf of Importer of Record / Full Legal Name:', 9, true, rgb(0.3, 0.3, 0.3));
      auditPage.drawText(authorizedSigner, {
        x: margin, y: curY, size: 24, font: boldFont, color: rgb(0.01, 0.12, 0.38),
      });
      curY -= 30;
      auditPage.drawLine({
        start: { x: margin, y: curY },
        end: { x: margin + 320, y: curY },
        thickness: 1,
        color: rgb(0.1, 0.1, 0.1),
      });
      curY -= 14;
      write(`${authorizedSigner} \u2014 Authorized Signer on behalf of ${importerOfRecord}`, 9, false, rgb(0.3, 0.3, 0.3));
      write(`Executed on: ${new Date(signedAt).toLocaleString('en-US', { timeZoneName: 'short' })}`, 9);
      curY -= 4;
      writeLine();

      write('TAMPER-EVIDENT SEAL', 10, true, rgb(0.05, 0.09, 0.13));
      write('This PDF has been encrypted and write-protected. Any modification invalidates this record.', 9, false, rgb(0.4, 0.4, 0.4));
      write('This audit trail constitutes valid legal proof of electronic signature execution.', 9, false, rgb(0.4, 0.4, 0.4));

      const ownerPassword = `${clientId}-${Date.now()}-${Math.random().toString(36).substring(2)}`;
      const pdfBytes = await pdfDoc.save({
        userPassword: '',
        ownerPassword,
        permissions: {
          printing: 'highResolution',
          modifying: false,
          copying: false,
          annotating: false,
          fillingForms: false,
          contentAccessibility: true,
          documentAssembly: false,
        },
      });

      const filePath = `${clientId}/engagement_letter_signed.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('signed-engagements')
        .upload(filePath, new Blob([pdfBytes], { type: 'application/pdf' }), {
          upsert: true,
          contentType: 'application/pdf',
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = await supabase.storage
        .from('signed-engagements')
        .createSignedUrl(filePath, 315360000);

      const letterUrl = urlData?.signedUrl || '';

      const { error: updateError } = await supabase
        .from('clients')
        .update({
          engagement_signed_at: signedAt,
          engagement_signature_ip: ipAddress,
          engagement_signature_name: authorizedSigner,
          engagement_letter_url: letterUrl,
          engagement_importer_of_record: importerOfRecord,
        })
        .eq('id', clientId);

      if (updateError) throw updateError;

      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            eventType: 'engagement_signed',
            clientName,
            clientEmail,
            signedAt,
            signatureIp: ipAddress,
            signatureName: authorizedSigner,
            timestamp: new Date().toISOString(),
          },
        });
      } catch (notifyErr) {
        console.warn('Admin notification failed:', notifyErr);
      }

      // Send confirmation email to the client with PDF download link + timestamp + next steps
      try {
        await supabase.functions.invoke('send-client-notification', {
          body: {
            eventType: 'engagement_signed_confirmation',
            clientName,
            clientEmail,
            signedAt,
            signatureName: authorizedSigner,
            importerOfRecord,
            letterUrl,
            timestamp: new Date().toISOString(),
          },
        });
      } catch (clientNotifyErr) {
        console.warn('Client confirmation email failed:', clientNotifyErr);
      }

      setSignedUrl(letterUrl);
      setStep('done');
      onSigned({ letterUrl, signedAt });
    } catch (err: unknown) {
      console.error('Signing error:', err);
      setError((err as Error).message || 'Failed to process your signature. Please try again.');
      setStep('review');
    }
  }, [isFormValid, clientId, clientEmail, authorizedSigner, importerOfRecord, templateUrl, onSigned, clientName]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.88)',
      overflowY: 'auto',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{
        background: '#0d1520',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '18px',
        width: '100%',
        maxWidth: '820px',
        marginBottom: '24px',
      }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(253,185,19,0.15)', borderRadius: '10px',
              border: '1px solid rgba(253,185,19,0.35)',
            }}>
              <i className="ri-file-text-fill" style={{ color: '#FDB913', fontSize: '20px' }}></i>
            </div>
            <div>
              <h2 style={{ color: 'white', fontWeight: 900, fontSize: '17px', margin: 0 }}>
                Engagement Letter
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', margin: 0 }}>
                Review &amp; sign to activate your case
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.4)', padding: '6px', borderRadius: '8px',
            }}
          >
            <i className="ri-close-line" style={{ fontSize: '22px' }}></i>
          </button>
        </div>

        {step === 'review' && (
          <div style={{ padding: '24px' }}>

            {/* ───── FIELD 1: Importer of Record ───── */}
            <div style={{
              background: 'rgba(253,185,19,0.07)',
              border: '2px solid rgba(253,185,19,0.5)',
              borderRadius: '14px',
              padding: '20px',
              marginBottom: '12px',
            }}>
              <p style={{
                color: '#FDB913', fontWeight: 900, fontSize: '11px',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                margin: '0 0 14px 0',
              }}>
                <i className="ri-quill-pen-line" style={{ marginRight: '6px' }}></i>
                Sign Your Engagement Letter
              </p>

              <label style={{
                display: 'block',
                color: '#FDB913', fontWeight: 900, fontSize: '13px',
                marginBottom: '6px',
              }}>
                Importer of Record <span style={{ color: '#f87171' }}>*</span>
              </label>
              <input
                type="text"
                value={importerOfRecord}
                onChange={(e) => setImporterOfRecord(e.target.value)}
                placeholder="Legal entity name of the Importer of Record"
                style={{
                  width: '100%', padding: '11px 14px',
                  background: 'rgba(0,0,0,0.45)',
                  border: '1px solid rgba(253,185,19,0.45)',
                  borderRadius: '8px', color: 'white', fontSize: '14px',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', margin: '5px 0 0 0' }}>
                Stamped above the signature line in the locked PDF
              </p>
            </div>

            {/* ───── FIELD 2: Authorized Signer ───── */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: '14px',
              padding: '20px',
              marginBottom: '12px',
            }}>
              <label style={{
                display: 'block',
                color: 'white', fontWeight: 900, fontSize: '13px',
                marginBottom: '6px',
              }}>
                Authorized Signer on behalf of Importer of Record / Full Legal Name{' '}
                <span style={{ color: '#f87171' }}>*</span>
              </label>
              <input
                type="text"
                value={authorizedSigner}
                onChange={(e) => setAuthorizedSigner(e.target.value)}
                placeholder="Type your full legal name"
                style={{
                  width: '100%', padding: '11px 14px',
                  background: 'rgba(0,0,0,0.45)',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '8px', color: 'white', fontSize: '15px',
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', margin: '5px 0 0 0' }}>
                Your electronic signature — appears above signature line in the locked PDF
              </p>
            </div>

            {/* ───── Consent Checkboxes ───── */}
            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {([
                {
                  state: hasRead,
                  toggle: () => setHasRead(v => !v),
                  text: 'I have carefully read and understood this Engagement Letter in its entirety and agree to its terms.',
                },
                {
                  state: consentEsign,
                  toggle: () => setConsentEsign(v => !v),
                  text: 'I consent to electronic transactions and signatures under the ESIGN Act (15 U.S.C. \u00a7\u00a07001) and UETA.',
                },
                {
                  state: consentIntent,
                  toggle: () => setConsentIntent(v => !v),
                  text: 'I expressly intend this electronic signature to constitute my legally binding signature.',
                },
              ] as { state: boolean; toggle: () => void; text: string }[]).map((item, idx) => (
                <div
                  key={idx}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}
                  onClick={item.toggle}
                >
                  <div style={{
                    marginTop: '2px', width: '18px', height: '18px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '4px',
                    border: item.state ? '2px solid #FDB913' : '2px solid rgba(255,255,255,0.25)',
                    background: item.state ? '#FDB913' : 'transparent',
                    transition: 'all 0.15s',
                  }}>
                    {item.state && <i className="ri-check-line" style={{ color: 'black', fontSize: '11px' }}></i>}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', lineHeight: 1.55 }}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px', padding: '10px 14px', color: '#f87171',
                fontSize: '13px', marginBottom: '14px',
              }}>
                <i className="ri-error-warning-line" style={{ marginRight: '6px' }}></i>{error}
              </div>
            )}

            {/* ───── Sign Button ───── */}
            <button
              onClick={handleSign}
              disabled={!isFormValid}
              style={{
                width: '100%', padding: '15px',
                borderRadius: '10px', fontWeight: 900, fontSize: '15px',
                border: 'none',
                cursor: isFormValid ? 'pointer' : 'not-allowed',
                background: isFormValid
                  ? 'linear-gradient(135deg, #FDB913, #e6a800)'
                  : 'rgba(255,255,255,0.06)',
                color: isFormValid ? 'black' : 'rgba(255,255,255,0.2)',
                transition: 'all 0.2s',
                marginBottom: '8px',
              }}
            >
              <i className="ri-quill-pen-line" style={{ marginRight: '8px' }}></i>
              Sign Engagement Letter
            </button>
            <p style={{
              color: 'rgba(255,255,255,0.2)', fontSize: '11px',
              textAlign: 'center', margin: '0 0 20px 0',
            }}>
              A tamper-evident ESIGN-compliant locked PDF will be saved to your account.
            </p>

            {/* ───── PDF Viewer ───── */}
            {templateUrl ? (
              <div style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px', overflow: 'hidden',
              }}>
                <div style={{
                  padding: '8px 14px',
                  background: 'rgba(255,255,255,0.04)',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <i className="ri-file-pdf-2-fill" style={{ color: '#f87171', fontSize: '14px' }}></i>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                    Read the full agreement before signing
                  </span>
                </div>
                <iframe
                  src={`${templateUrl}#toolbar=0`}
                  title="Engagement Letter"
                  style={{ width: '100%', height: '500px', display: 'block', border: 'none' }}
                />
              </div>
            ) : (
              <div style={{
                border: '1px solid rgba(253,185,19,0.25)',
                borderRadius: '12px', padding: '20px',
                background: 'rgba(253,185,19,0.04)',
                textAlign: 'center',
              }}>
                <i className="ri-file-pdf-2-line" style={{ color: 'rgba(253,185,19,0.4)', fontSize: '32px', display: 'block', marginBottom: '8px' }}></i>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 }}>
                  Your attorney will upload the engagement letter template shortly.
                  You may complete your signature now — it will be applied to the document.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── PROCESSING ── */}
        {step === 'processing' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 24px', textAlign: 'center',
          }}>
            <div style={{
              width: '52px', height: '52px',
              border: '4px solid #FDB913', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              marginBottom: '24px',
            }}></div>
            <h3 style={{ color: 'white', fontWeight: 900, fontSize: '20px', marginBottom: '8px' }}>
              Generating Your Signed Document
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', maxWidth: '360px' }}>
              Creating your ESIGN-compliant locked PDF with full audit trail...
            </p>
          </div>
        )}

        {/* ── DONE ── */}
        {step === 'done' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 24px', textAlign: 'center',
          }}>
            <div style={{
              width: '72px', height: '72px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(34,197,94,0.15)', borderRadius: '50%',
              border: '1px solid rgba(34,197,94,0.35)', marginBottom: '24px',
            }}>
              <i className="ri-checkbox-circle-fill" style={{ color: '#4ade80', fontSize: '44px' }}></i>
            </div>
            <h3 style={{ color: 'white', fontWeight: 900, fontSize: '24px', marginBottom: '8px' }}>
              Engagement Letter Signed
            </h3>
            <p style={{
              color: 'rgba(255,255,255,0.55)', fontSize: '14px',
              maxWidth: '360px', marginBottom: '24px',
            }}>
              Your signature has been recorded and a tamper-evident locked PDF has been saved to your account.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {signedUrl && (
                <a
                  href={signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white', fontWeight: 700, fontSize: '14px',
                    padding: '10px 20px', borderRadius: '10px',
                    textDecoration: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  <i className="ri-download-line"></i>Download Signed PDF
                </a>
              )}
              <button
                onClick={onClose}
                style={{
                  background: '#FDB913', border: 'none', color: 'black',
                  fontWeight: 900, fontSize: '14px', padding: '10px 24px',
                  borderRadius: '10px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Continue to Portal
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
